import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { emitFlashMessage, subscribeToFlashMessages } from "../utils/flashMessageService";

const FlashMessageContext = createContext(null);

export const useFlashMessage = () => {
  const context = useContext(FlashMessageContext);
  if (!context) {
    throw new Error("useFlashMessage must be used within a FlashMessageProvider");
  }
  return context;
};

export const FlashMessageProvider = ({ children }) => {
  const [message, setMessage] = useState(null);
  const timeoutRef = useRef(null);

  const clearMessage = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setMessage(null);
  }, []);

  const displayMessage = useCallback(
    ({ text, type = "info", timeout = 4000 } = {}) => {
      if (!text) return;
      clearMessage();
      setMessage({ id: Date.now(), text, type });
      if (timeout !== null) {
        timeoutRef.current = setTimeout(() => {
          clearMessage();
        }, Number(timeout));
      }
    },
    [clearMessage]
  );

  useEffect(() => {
    const unsubscribe = subscribeToFlashMessages(displayMessage);
    return () => {
      unsubscribe();
      clearMessage();
    };
  }, [displayMessage, clearMessage]);

  const showMessage = useCallback((text, options = {}) => {
    emitFlashMessage({ text, ...options });
  }, []);

  const contextValue = useMemo(
    () => ({ showMessage, clearMessage }),
    [showMessage, clearMessage]
  );

  const typeStyles = {
    success: "bg-emerald-600",
    error: "bg-rose-600",
    info: "bg-sky-600",
  };

  const currentStyle = message ? typeStyles[message.type] || typeStyles.info : "";
  const ariaLive = message?.type === "error" ? "assertive" : "polite";

  return (
    <FlashMessageContext.Provider value={contextValue}>
      {children}
      <div className="fixed inset-x-0 top-4 z-[100] flex justify-center pointer-events-none">
        {message && (
          <div
            key={message.id}
            role="status"
            aria-live={ariaLive}
            className={`pointer-events-auto px-4 py-3 rounded-lg shadow-lg text-white font-semibold flex items-center gap-3 ${currentStyle}`}
          >
            <span>{message.text}</span>
            <button
              type="button"
              onClick={clearMessage}
              className="ml-2 rounded-full border border-white/60 px-2 py-0.5 text-xs font-medium text-white/90 hover:bg-white/10"
              aria-label="Dismiss notification"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </FlashMessageContext.Provider>
  );
};

export default FlashMessageProvider;
